import { useHistory, useLocation, useRouteMatch } from "react-router-dom";
import BankCircular from "static/images/BankCircular.svg";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { Button } from "components/DesignSystem/Button/Button";
import ThreeDots from "static/images/ThreeDots.svg";
import { useModal } from "hooks/useModal";
import { useDispatch } from "react-redux";
import { openEditBankModal } from "store/slices/connections";
import { Cross } from "components/icons/Cross";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import DoubleGreenTick from "static/images/DoubleGreenTick.svg";
import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import dayjs from "dayjs";
import ToolTip from "components/design/toolTip";
import { DisconnectBankModal } from "components/Connections/DisconnectBankModal";
import { DeleteBankModal } from "components/ComplianceTable/DeleteBankModal";

import { useGetEntityBanksQuery } from "store/apis/bankConnections";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";

import Slider from "components/DesignSystem/Slider/Slider";
import relativeTime from "dayjs/plugin/relativeTime";
import Modal from "components/DesignSystem/Modal/Modal";

import { BANK_ACCOUNT_STATUS } from "constants/dataSource";
import { useToast } from "hooks/useToast";
import { BackendError } from "types/utils/error";
import { BankConnection } from "types/Models/banks";
import { ReactNode } from "react";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { LockedUi } from "components/LockedUi/LockedUi";
import { ReviewNewBankAccounts } from "./ReviewNewBankAccounts";
import { BankAccountItem } from "./BankAccountItem";
import { PlaidManageAccounts } from "./PlaidManageAccounts";
import { DirectManageAccounts } from "./DirectManageAccounts";
import { pluralize } from "utils/pluralize";
import { Info } from "components/icons/Info";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { TransactionComboboxControl } from "components/Transaction/TransactionColumn";
import { SingleValue } from "react-select";
import {
  useUpdateBankAccountantAccessMutation,
  useRequestAccountantAccessMutation,
} from "store/apis/bankConnections";
import { bankAccessOptions } from "components/BooksCrm/EntityBankConnections";
import classNames from "classnames";
import { DISPLAY_DATE } from "constants/date";
import { getConnectionInfoItem } from "../DataSourcesList";
import { useBankConnect } from "hooks/useBankConnect";
import {
  BANK_CONNECTED_BROADCAST_MESSAGE,
  BANK_CONNECTION_SUCCESS_BROADCAST_CHANNEL,
} from "constants/connections";
import { useBroadcastChannel } from "hooks/useBroadcastChannel";
import { useUpdateQuery } from "hooks/useQuery";
import { ReConnect } from "components/Connections/ReConnect";
import { UnAvailableCloud } from "components/icons/UnAvailableCloud";
import { getDisconnectedMessage } from "utils/bankNameAcc";
import { colors } from "constants/colors";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { GrantAccountantAccessModal } from "components/OnboardingWidget/Tax/GrantAccountantAccessModal";

dayjs.extend(relativeTime);

const BankAccountTitle = ({
  title,
  cta,
}: {
  title: ReactNode;
  cta?: ReactNode;
}) => {
  return (
    <div className="t-flex t-items-center t-gap-3">
      <div className="t-text-subtext-sm t-text-text-30">{title}</div>
      <div className="t-bg-line-gradient t-h-px t-flex-1"></div>
      {cta && cta}
    </div>
  );
};

export const DataSourceView = ({
  connection,
  onClose,
}: {
  connection: BankConnection;
  onClose(): void;
}) => {
  const history = useHistory();
  const { search } = useLocation();
  const { url } = useRouteMatch();
  const { uuid: groupId, entities } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { alertToast, successToast } = useToast();
  const { isAdmin, isCustomer } = useRoleBasedView();
  const [updateBankAccountantAccess, { isLoading: isUpdatingBankAccess }] =
    useUpdateBankAccountantAccessMutation();
  const [requestAccountantAccess, { isLoading: isRequestingAccess }] =
    useRequestAccountantAccessMutation();

  const currentEntity = entities.find((e) => e.uuid === entityId);
  const entityName = currentEntity?.name || "";
  const bookkeepingChannelId = currentEntity?.bookkeeping_channel || null;

  const { onConnect, isLoading: isConnectBankLoading } = useBankConnect();

  const { isFetching } = useGetEntityBanksQuery(
    {
      groupId: groupId!,
      entityId: entityId!,
    },
    { skip: !groupId || !entityId }
  );

  const dispatch = useDispatch();

  const disconnectModalState = useModal();
  const deleteBankModalState = useModal();

  const activeManualAccounts = connection.bank_accounts.filter(
    (account) =>
      account.mark_as_manually_added &&
      account.status !== BANK_ACCOUNT_STATUS.CLOSED
  );

  const closedAccounts = connection.bank_accounts.filter(
    (account) => account.status === BANK_ACCOUNT_STATUS.CLOSED
  );

  const activeAccounts = connection.bank_accounts.filter(
    (account) =>
      account.status !== BANK_ACCOUNT_STATUS.CLOSED &&
      !account.mark_as_manually_added
  );

  const newAccounts = connection.bank_accounts.filter(
    (account) => account.is_new_bank_account
  );

  const { isAllAccountsClosed } = getConnectionInfoItem({ connection });

  const manageAccountsState = useModal();
  const reviewModal = useModal();
  const plaidUpdateModal = useModal();
  const grantAccessModal = useModal();

  const handleBankAccessUpdate = async (accountantAccess?: string) => {
    try {
      await updateBankAccountantAccess({
        entityId: entityId!,
        accountantAccess: accountantAccess === "true",
        entityItemId: connection.entity_item_id,
      }).unwrap();
      successToast({ message: "Access updated successfully" });
    } catch (e) {
      alertToast({ message: (e as BackendError).data?.error?.message });
    }
  };

  const handleConfirmAccessGranted = async () => {
    await handleBankAccessUpdate("true");
    grantAccessModal.close();
  };

  const handleRequestAccess = async () => {
    if (!bookkeepingChannelId) return;

    try {
      await requestAccountantAccess({
        entityId: entityId!,
        entityItemIds: [connection.entity_item_id],
        channelId: bookkeepingChannelId,
      }).unwrap();

      successToast({ message: "Accountant access requested successfully" });
      history.push(`/admin/chat/${bookkeepingChannelId}`);
    } catch (e) {
      alertToast({ message: (e as BackendError).data?.error?.message });
    }
  };

  const selectedValue = bankAccessOptions.find(
    (option) => option.value === connection.accountant_access?.toString()
  );

  const disconnectedMessage = getDisconnectedMessage(connection);

  return (
    <>
      <Slider.Root
        onOpenChange={onClose}
        open={Boolean(connection)}
        modal={false}
      >
        <Slider.Content useCustomOverlay>
          {connection && (
            <>
              <div className="t-flex t-flex-col t-h-full">
                <div className="t-flex t-px-5 py-3 t-border-0 t-items-center t-border-b t-border-solid t-border-neutral-0 t-sticky t-top-0 t-z-10 t-bg-surface t-flex-shrink-0">
                  {connection?.logo_url ? (
                    <img
                      src={connection.logo_url}
                      className="t-rounded-full t-w-10 t-h-10"
                      alt=""
                    />
                  ) : (
                    <img
                      src={BankCircular}
                      className="t-rounded-full t-w-10 t-h-10"
                      alt=""
                    />
                  )}

                  <p className="t-m-0 t-ml-2">{connection?.name}</p>

                  <div className="t-flex t-gap-1 t-ml-auto">
                    <LockedUi feature={WRITE_PERMISSION_KEYS.DATA_SOURCES}>
                      <Dropdown.Root>
                        <Dropdown.Trigger asChild>
                          <div>
                            <Button
                              customType="ghost_icon"
                              size="small"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <img src={ThreeDots} alt="" />
                            </Button>
                          </div>
                        </Dropdown.Trigger>
                        <Dropdown.Content align="end">
                          {/* <Dropdown.Item>Re-sync</Dropdown.Item> */}
                          {connection.connection_type === "MANUAL" && (
                            <>
                              <Dropdown.Item
                                onSelect={() =>
                                  dispatch(
                                    openEditBankModal({
                                      editBankId: connection.uuid,
                                    })
                                  )
                                }
                                onClick={(e) => e.stopPropagation()}
                              >
                                Edit
                              </Dropdown.Item>
                              <Dropdown.Item
                                onClick={(e) => e.stopPropagation()}
                                onSelect={deleteBankModalState.open}
                              >
                                <p className="t-m-0 t-text-red">Delete</p>
                              </Dropdown.Item>
                            </>
                          )}
                          {(connection.connection_type === "PLAID" ||
                            connection.connection_type === "DIRECT") && (
                            <>
                              {connection.is_connection_available && (
                                <Dropdown.Item
                                  onSelect={disconnectModalState.open}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <p className="t-m-0">Disconnect</p>
                                </Dropdown.Item>
                              )}
                              <Dropdown.Item
                                onClick={(e) => e.stopPropagation()}
                                onSelect={deleteBankModalState.open}
                              >
                                <p className="t-m-0 t-text-red">Delete</p>
                              </Dropdown.Item>
                            </>
                          )}
                        </Dropdown.Content>
                      </Dropdown.Root>
                    </LockedUi>
                    <Button
                      customType="ghost_icon"
                      size="small"
                      onClick={onClose}
                      isLoading={isFetching}
                      disabled={isFetching}
                    >
                      <Cross color="currentColor" />
                    </Button>
                  </div>
                </div>
                <Slider.Body>
                  <div className="t-flex t-flex-col t-gap-4">
                    <div className="t-flex t-flex-col t-gap-0.5">
                      <div className="t-flex t-flex-row t-justify-start t-items-start">
                        <div className="t-flex t-flex-col t-gap-1 t-w-1/2">
                          <span className="t-text-text-30 t-text-body-sm">
                            Total Balance
                          </span>
                          <span className="t-text-body-sm t-text-text-100">
                            <AmountSuperScript
                              amount={
                                connection?.bank_accounts?.reduce(
                                  (acc, account) => {
                                    return account.status ===
                                      BANK_ACCOUNT_STATUS.CLOSED
                                      ? acc
                                      : acc + account.available_balance;
                                  },
                                  0
                                ) || 0
                              }
                            />
                          </span>
                        </div>

                        {isCustomer && (
                          <div className="t-flex t-flex-col t-items-start t-gap-1 t-w-1/2">
                            <span className="t-text-text-30 t-text-body-sm">
                              Accountant Access
                            </span>

                            {connection.accountant_access ? (
                              <Tag tagType="green" icon={false}>
                                Access granted
                              </Tag>
                            ) : (
                              <Button
                                customType="primary_outlined"
                                size="small"
                                onClick={grantAccessModal.open}
                              >
                                Grant access
                              </Button>
                            )}
                            <GrantAccountantAccessModal
                              open={grantAccessModal.isOpen}
                              bankName={connection.name}
                              isLoading={isUpdatingBankAccess}
                              onCancel={grantAccessModal.close}
                              onConfirm={handleConfirmAccessGranted}
                            />
                          </div>
                        )}

                        {!isCustomer && (
                          <div className="t-flex t-flex-col t-gap-1 t-w-1/2">
                            <span className="t-text-text-30 t-text-body-sm">
                              Accountant Access
                            </span>

                            {isAdmin ? (
                              <div className="t-flex t-flex-col t-gap-1">
                                <div className="t-w-20">
                                  <Combobox
                                    size="small"
                                    menuPlacement="auto"
                                    creatable={false}
                                    onChange={(prop) =>
                                      handleBankAccessUpdate(
                                        (prop as SingleValue<OptionData>)?.value
                                      )
                                    }
                                    components={{
                                      Control: TransactionComboboxControl,
                                      ClearIndicator: () => null,
                                    }}
                                    options={bankAccessOptions}
                                    defaultValue={selectedValue || null}
                                    menuPortalTarget={document.body}
                                  />
                                </div>
                                {!connection.accountant_access &&
                                  connection.accountant_access_reason && (
                                    <span className="t-text-caption-italic t-text-text-30 t-italic">
                                      Reason:{" "}
                                      {connection.accountant_access_reason}
                                    </span>
                                  )}
                                <Button
                                  customType="link"
                                  size="small"
                                  onClick={handleRequestAccess}
                                  isLoading={isRequestingAccess}
                                  disabled={!bookkeepingChannelId}
                                >
                                  Request Access
                                </Button>
                              </div>
                            ) : (
                              <div className="t-flex t-gap-1 t-items-center">
                                <div
                                  className={classNames(
                                    "t-h-1.5 t-w-1.5 t-rounded-full",
                                    {
                                      "t-bg-dark_green":
                                        connection.accountant_access,
                                      "t-bg-red": !connection.accountant_access,
                                    }
                                  )}
                                />
                                <div className="t-text-body-sm t-text-text-100">
                                  {connection.accountant_access ? "Yes" : "No"}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      {!connection?.is_connection_available ? (
                        <Banner
                          type="error"
                          icon={
                            <Info
                              color={colors.red.DEFAULT}
                              stroke="1.2"
                              size="16"
                            />
                          }
                          description={disconnectedMessage}
                          cta={
                            !isAllAccountsClosed && (
                              <LockedUi
                                feature={WRITE_PERMISSION_KEYS.DATA_SOURCES}
                              >
                                <ReConnect
                                  type={
                                    connection.update_available
                                      ? "UPDATE"
                                      : "RECONNECT"
                                  }
                                  isLoading={isConnectBankLoading}
                                  onConnect={onConnect}
                                  connection={connection}
                                  custom_type="link"
                                />
                              </LockedUi>
                            )
                          }
                        />
                      ) : (
                        connection?.last_successful_transaction_update && (
                          <span className="t-flex t-gap-1 t-items-center">
                            <img src={DoubleGreenTick} alt="Connected" />
                            <span className="t-text-text-30 t-text-body-sm">
                              Updated{" "}
                              {dayjs(
                                connection?.last_successful_transaction_update
                              ).fromNow()}
                            </span>
                          </span>
                        )
                      )}
                    </div>

                    {newAccounts.length > 0 && (
                      <Banner
                        description={`${newAccounts.length} new ${pluralize(
                          newAccounts.length,
                          "account is",
                          "accounts are",
                          false
                        )} now syncing from the bank. Review and manage them if needed.`}
                        type="info"
                        cta={
                          <Modal.Root
                            open={reviewModal.isOpen}
                            onOpenChange={reviewModal.toggle}
                          >
                            <Modal.Trigger asChild>
                              <Button size="small">View</Button>
                            </Modal.Trigger>
                            <Modal.Content>
                              <ReviewNewBankAccounts
                                onManage={manageAccountsState.open}
                                close={reviewModal.close}
                                entityItemId={connection.uuid}
                                accounts={connection.bank_accounts.filter(
                                  (account) => account.is_new_bank_account
                                )}
                              />
                            </Modal.Content>
                          </Modal.Root>
                        }
                      />
                    )}

                    {connection.update_available && (
                      <Banner
                        description="New accounts detected in the bank. Update in Plaid to start syncing them into Inkle Books."
                        type="warning"
                        cta={
                          <LockedUi
                            feature={WRITE_PERMISSION_KEYS.DATA_SOURCES}
                          >
                            <Button
                              size="small"
                              customType="primary"
                              onClick={plaidUpdateModal.open}
                            >
                              Update
                            </Button>
                            <PlaidManageAccounts
                              isOpen={plaidUpdateModal.isOpen}
                              close={plaidUpdateModal.close}
                              connection={connection}
                            />
                          </LockedUi>
                        }
                      />
                    )}

                    {activeAccounts?.length > 0 && (
                      <div className="t-flex t-flex-col t-gap-3">
                        <BankAccountTitle
                          title="Active Accounts"
                          cta={
                            (connection.connection_type === "PLAID" ||
                              connection.connection_type === "DIRECT") && (
                              <LockedUi
                                feature={WRITE_PERMISSION_KEYS.DATA_SOURCES}
                              >
                                <Button
                                  customType="link"
                                  onClick={manageAccountsState.open}
                                  size="small"
                                >
                                  Manage
                                </Button>
                              </LockedUi>
                            )
                          }
                        />
                        <Accordion.Root
                          className="t-flex t-flex-col t-gap-4"
                          type="multiple"
                        >
                          {activeAccounts.map((account) => (
                            <BankAccountItem
                              key={account.uuid}
                              account={account}
                              connection={connection}
                            />
                          ))}
                        </Accordion.Root>
                      </div>
                    )}

                    {activeManualAccounts?.length > 0 && (
                      <div className="t-flex t-flex-col t-gap-3">
                        <BankAccountTitle
                          title={
                            <div className="t-flex t-gap-1 t-items-center">
                              <span>Manual Uploads</span>
                              <ToolTip
                                text="This account is no longer syncing automatically. You can continue tracking it by manually uploading transactions."
                                collisionPadding={16}
                                propClass="t-max-w-[500px] sm:t-max-w-72"
                              >
                                <span>
                                  <Info size="16" />
                                </span>
                              </ToolTip>
                            </div>
                          }
                        />
                        <Accordion.Root
                          className="t-flex t-flex-col t-gap-4"
                          type="multiple"
                        >
                          {activeManualAccounts.map((account) => (
                            <BankAccountItem
                              key={account.uuid}
                              account={account}
                              connection={connection}
                            />
                          ))}
                        </Accordion.Root>
                      </div>
                    )}

                    {closedAccounts?.length > 0 && (
                      <div className="t-flex t-flex-col t-gap-3">
                        <BankAccountTitle title="Closed accounts" />
                        <Accordion.Root
                          className="t-flex t-flex-col t-gap-4"
                          type="multiple"
                        >
                          {closedAccounts.map((account) => (
                            <BankAccountItem
                              key={account.uuid}
                              account={account}
                              connection={connection}
                            />
                          ))}
                        </Accordion.Root>
                      </div>
                    )}
                  </div>
                </Slider.Body>
              </div>
            </>
          )}

          {connection.connection_type === "PLAID" &&
            manageAccountsState.isOpen && (
              <PlaidManageAccounts
                connection={connection}
                isOpen={manageAccountsState.isOpen}
                close={manageAccountsState.close}
              />
            )}

          {connection.connection_type === "DIRECT" &&
            manageAccountsState.isOpen && (
              <DirectManageAccounts
                connection={connection}
                isOpen={manageAccountsState.isOpen}
                close={manageAccountsState.close}
              />
            )}

          <DisconnectBankModal
            show={disconnectModalState.isOpen}
            onClose={() => {
              disconnectModalState.close();
              history.push(`${url}${search}`);
            }}
            connection={connection}
          />
          <DeleteBankModal
            isOpen={deleteBankModalState.isOpen}
            close={deleteBankModalState.close}
            connection={connection}
          />
        </Slider.Content>
      </Slider.Root>
    </>
  );
};
